/**
 * Source Type validation utilities
 * Provides validation functions for source type form fields according to business rules
 */

/**
 * Extracts numeric value from mixed input (handles paste with non-numeric content)
 * @param {string|number} value - The input value
 * @returns {string|null} - Extracted numeric value or null if no valid number found
 */
function extractNumericValue(value) {
  if (value === null || value === undefined) {
    return null
  }
  
  const stringValue = String(value)
  const numericMatch = stringValue.match(/\d+(\.\d+)?/)
  return numericMatch ? numericMatch[0] : null
}

/**
 * Validates source type name according to business rules
 * @param {string} name - The name to validate
 * @param {Function} $t - Translation function for error messages
 * @param {Array} existingSourceTypes - Array of existing source type names
 * @param {boolean} isEdit - Whether this is an edit operation
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validateSourceTypeName(name, $t, existingSourceTypes = [], isEdit = false) {
  if (!name || name.trim() === '') {
    return {
      isValid: false,
      error: $t('sourceTypes.validation.nameRequired')
    }
  }

  const trimmedName = name

  // Length validation
  if (trimmedName.length < 3) {
    return {
      isValid: false,
      error: $t('sourceTypes.validation.nameTooShort')
    }
  }

  if (trimmedName.length > 30) {
    return {
      isValid: false,
      error: $t('sourceTypes.validation.nameTooLong')
    }
  }

  if (/\s/.test(trimmedName)) {
    return {
      isValid: false,
      error: $t('sourceTypes.validation.nameInvalidChars')
    }
  }

  // Character validation - only letters, numbers, hyphens, underscores, and dots
  if (!/^[a-zA-Z0-9._-]+$/.test(trimmedName)) {
    return {
      isValid: false,
      error: $t('sourceTypes.validation.nameInvalidChars')
    }
  }

  // Uniqueness validation (case-insensitive)
  if (!isEdit && existingSourceTypes.some(existing => 
    existing.toLowerCase() === trimmedName.toLowerCase())) {
    return {
      isValid: false,
      error: $t('sourceTypes.validation.nameExists')
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Validates and truncates text fields with character limits
 * @param {string} value - The text value to validate
 * @param {number} maxLength - Maximum allowed length
 * @param {Function} $t - Translation function for error messages
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} - Validation result with isValid boolean, error message, and truncated value
 */
export function validateTextFieldWithLimit(value, maxLength, $t, fieldName) {
  const currentLength = value ? value.length : 0
  let truncatedValue = value

  if (currentLength > maxLength) {
    truncatedValue = value.substring(0, maxLength)
    return {
      isValid: true,
      error: '',
      truncatedValue,
      wasTruncated: true,
      characterCount: maxLength,
      maxLength
    }
  }

  return {
    isValid: true,
    error: '',
    truncatedValue: value,
    wasTruncated: false,
    characterCount: currentLength,
    maxLength
  }
}

/**
 * Validates required text fields
 * @param {string} value - The value to validate
 * @param {Function} $t - Translation function for error messages
 * @param {string} validationKey - Translation key for the required field error
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validateRequiredField(value, $t, validationKey) {
  if (!value || value.trim() === '') {
    return {
      isValid: false,
      error: $t(validationKey)
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Validates CSV fields list
 * @param {Array} fields - Array of field objects
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validateCSVFields(fields, $t) {
  if (!fields || fields.length === 0) {
    return {
      isValid: false,
      error: $t('sourceTypes.validation.fieldsRequired')
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Validates conditional fields based on form state
 * @param {Object} formData - The form data object
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with field-specific errors
 */
export function validateConditionalFields(formData, $t) {
  const errors = {}

  // CSV format specific validations
  if (formData.type === 'csv') {
    // Field separator is required for CSV
    if (!formData.field_delimiter) {
      errors.field_delimiter = $t('sourceTypes.validation.fieldSeparatorRequired')
    } else if (/\s/.test(formData.field_delimiter)) {
      // Field separator cannot contain spaces
      errors.field_delimiter = $t('sourceTypes.validation.fieldSeparatorNoSpaces')
    }
  }

  // Custom format specific validations
  if (formData.type === 'custom') {
    // RegExp is required for custom format
    if (!formData.regex_string || formData.regex_string.trim() === '') {
      errors.regex_string = $t('sourceTypes.validation.regexpRequired')
    }
  }

  // Time field validations when not using auto-fill
  if (!formData.time_fill) {
    if (!formData.time_field || formData.time_field.trim() === '') {
      errors.time_field = $t('sourceTypes.validation.timeFieldRequired')
    } else if (/\s/.test(formData.time_field)) {
      // Time field cannot contain spaces
      errors.time_field = $t('sourceTypes.validation.timeFieldNoSpaces')
    }
    if (!formData.time_format || formData.time_format.trim() === '') {
      errors.time_format = $t('sourceTypes.validation.timeFormatRequired')
    }
  }

  // Event separator is required for all formats
  if (formData.type && !formData.event_delimiter) {
    errors.event_delimiter = $t('sourceTypes.validation.eventSeparatorRequired')
  } else if (formData.type && /\s/.test(formData.event_delimiter)) {
    // Event separator cannot contain spaces
    errors.event_delimiter = $t('sourceTypes.validation.eventSeparatorNoSpaces')
  }

  // Comment validation - cannot contain spaces
  if (formData.comment && /\s/.test(formData.comment)) {
    errors.comment = $t('sourceTypes.validation.commentNoSpaces')
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

/**
 * Validates all source type form fields
 * @param {Object} formData - The form data object
 * @param {Function} $t - Translation function for error messages
 * @param {Array} existingSourceTypes - Array of existing source type names
 * @param {boolean} isEdit - Whether this is an edit operation
 * @returns {Object} - Validation result with overall validity and field-specific errors
 */
export function validateSourceTypeForm(formData, $t, existingSourceTypes = [], isEdit = false) {
  const nameValidation = validateSourceTypeName(formData.name, $t, existingSourceTypes, isEdit)
  const conditionalValidation = validateConditionalFields(formData, $t)
  
  // CSV fields validation
  let csvFieldsValidation = { isValid: true, error: '' }
  if (formData.type === 'csv') {
    const fields = formData.field_names ? formData.field_names.split(',').filter(name => name.trim()) : []
    csvFieldsValidation = validateCSVFields(fields, $t)
  }

  const isValid = nameValidation.isValid && 
                  conditionalValidation.isValid && 
                  csvFieldsValidation.isValid

  return {
    isValid,
    errors: {
      name: nameValidation.error,
      csv_fields: csvFieldsValidation.error,
      ...conditionalValidation.errors
    }
  }
}

/**
 * Applies character limits and truncation to form data
 * @param {Object} formData - The form data object to process
 * @returns {Object} - Object with truncated values and truncation flags
 */
export function applyCharacterLimits(formData) {
  const limits = {
    description: 4096,
    event_delimiter: 4096,
    comment: 4096,
    field_delimiter: 4096,
    regex_string: 4096,
    time_field: 4096,
    time_format: 4096
  }

  const result = {
    truncatedValues: {},
    truncationFlags: {}
  }

  Object.keys(limits).forEach(field => {
    if (formData[field] && formData[field].length > limits[field]) {
      result.truncatedValues[field] = formData[field].substring(0, limits[field])
      result.truncationFlags[field] = true
    } else {
      result.truncatedValues[field] = formData[field]
      result.truncationFlags[field] = false
    }
  })

  return result
}
